import React, { useEffect, useState } from "react";
import { Button, Dialog } from "@mui/material";
import { Minus, Send, X } from "lucide-react";
import axios from "axios";
import {
  getOrgId,
  getStoredUser,
  getUserId,
} from "../../assistant/orgHelpers";

const ALEX_AVATAR = "https://alexbreen.com/profile.jpeg";

function getUserEmail() {
  const user = getStoredUser();
  if (!user) return "";
  return typeof user.email === "string" ? user.email : user.email?.S || "";
}

/**
 * Email-style compose modal for contacting Twilanswer support (Alex).
 * Prefill via initialSubject / initialBody when opened by the assistant.
 */
export default function SupportComposeModal({
  open,
  setOpen,
  initialSubject = "",
  initialBody = "",
  onSent,
}) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject(initialSubject || "");
    setBody(initialBody || "");
    setError(null);
    setSent(false);
    setSubmitting(false);
  }, [open, initialSubject, initialBody]);

  const canSubmit =
    subject.trim().length > 0 && body.trim().length > 0 && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        subject: subject.trim(),
        body: body.trim(),
      };
      const email = getUserEmail();
      const orgId = getOrgId();
      const userId = getUserId();
      if (email) payload.user_email = email;
      if (orgId) payload.org_id = orgId;
      if (userId) payload.user_id = userId;

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}support`, payload);
      setSent(true);
      if (typeof onSent === "function") {
        onSent({ subject: payload.subject });
      }
      setTimeout(() => {
        setOpen(false);
      }, 1200);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setError("Please enter both a subject and a message.");
      } else if (status === 502 || status === 503) {
        setError("Support is temporarily unavailable. Please try again shortly.");
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to send. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      disableEnforceFocus
      BackdropProps={{
        style: { backgroundColor: "rgba(15, 23, 42, 0.35)" },
      }}
      PaperProps={{
        sx: {
          width: { xs: "94vw", sm: 540 },
          maxWidth: 540,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow:
            "0 24px 48px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.06)",
          m: 2,
        },
      }}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#F3F4F6] border-b border-gray-200">
        <div className="text-[13px] font-semibold text-gray-800">
          New message
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Minimize"
            className="p-1 rounded hover:bg-gray-200 text-gray-500"
            onClick={handleClose}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            aria-label="Close"
            className="p-1 rounded hover:bg-gray-200 text-gray-500"
            onClick={handleClose}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {sent ? (
        <div className="px-5 py-12 text-center">
          <div className="mx-auto mb-3 w-12 h-12 rounded-full overflow-hidden ring-2 ring-green-100">
            <img
              src={ALEX_AVATAR}
              alt="Alex"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-[15px] font-semibold text-gray-800">
            Message sent
          </div>
          <p className="text-[13px] text-gray-500 mt-1">
            Alex will get back to you soon.
          </p>
        </div>
      ) : (
        <>
          {/* To */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <span className="text-[12px] font-medium text-gray-400 w-10 shrink-0">
              To
            </span>
            <div className="flex items-center gap-2.5 min-w-0">
              <img
                src={ALEX_AVATAR}
                alt="Alex"
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-gray-900 leading-tight">
                  Alex
                </div>
                <div className="text-[11px] text-gray-500 leading-tight">
                  Twilanswer Founder
                </div>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100">
            <span className="text-[12px] font-medium text-gray-400 w-10 shrink-0">
              Subject
            </span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              className="flex-1 min-w-0 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
              autoFocus
            />
          </div>

          {/* Body */}
          <div className="px-4 pt-3 pb-2">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message…"
              rows={9}
              className="w-full resize-none text-[14px] leading-relaxed text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
            />
          </div>

          {error && (
            <div className="px-4 pb-2">
              <p className="text-[12px] text-red-600">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-[#FAFAFA]">
            <p className="text-[11px] text-gray-400 hidden sm:block">
              Replies go to your account email
            </p>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              startIcon={<Send className="w-3.5 h-3.5" />}
              sx={{
                borderRadius: "999px",
                px: 2.5,
                py: "6px",
                fontSize: "13px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                ml: "auto",
                "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
              }}
            >
              {submitting ? "Sending…" : "Send"}
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
}
